/**
 * English strings — the source of truth for the key list.
 *
 * ===========================================================================
 * KEY CONVENTION (read this before adding a key; eleven other agents follow it)
 * ===========================================================================
 *
 * 1. SHAPE — namespaced dotted keys mirroring `src/`:
 *
 *        <area>.<screen-or-component>.<element>
 *
 *    `<area>` is the folder the string is rendered from — `auth`, `account`,
 *    `dashboard`, `nav`, `api` — plus two cross-cutting areas that belong to
 *    nobody: `common` (verbs and UI furniture reused everywhere) and `domain`
 *    (the product vocabulary below). `<screen-or-component>` is the component
 *    that owns the string, lowerCamelCase, dropping the `Page`/`Form` suffix
 *    when it reads better (`login`, `backupCodes`, `table`). `<element>` says
 *    what the string *is* (`title`, `submit`, `emptyFiltered`), never what it
 *    says — rename the copy, keep the key.
 *
 *    Two real keys, in full:
 *
 *        'auth.login.title'              -> 'Sign in'
 *        'dashboard.table.emptyFiltered'  -> 'No endpoints match these filters.'
 *
 *    Keys are FLAT strings, not nested objects. That is deliberate: it makes
 *    `keyof typeof en` a plain union, so `t()` autocompletes every key and
 *    `zh.ts` is a `Record<LocaleKey, string>` the compiler refuses to let you
 *    leave incomplete. A missing Chinese string is a failed `npm run build`,
 *    not a page that silently stays English.
 *
 * 2. INTERPOLATION — `{{name}}`, double braces, and you pass the value:
 *
 *        'dashboard.detail.sampledNote': 'Sampled: 1 in {{rate}} requests.'
 *        t('dashboard.detail.sampledNote', { rate: 10 })
 *
 *    Values are `string | number` and are inserted VERBATIM — the runtime does
 *    no number formatting, because this app formats its own numbers (`1,250`,
 *    `12.4%`). Format first, interpolate second:
 *
 *        t('dashboard.overview.totalRequests', { total: fmt(1250) })
 *
 *    An unknown placeholder is left alone rather than blanked, so a typo shows
 *    up as `{{totl}}` in the UI instead of disappearing.
 *
 * 3. PLURALS — suffix the key `_one` and `_other`, call it WITHOUT the suffix
 *    and pass `count`:
 *
 *        'common.requestCount_one':   '{{count}} request'
 *        'common.requestCount_other': '{{count}} requests'
 *        t('common.requestCount', { count: n })   // n === 1 ? _one : _other
 *
 *    `count` is also available as `{{count}}`, and may be a number or an
 *    already-formatted numeric string — `{ count: fmt(1250) }` renders
 *    "1,250 requests", because the digits, not the type, pick the form. Both
 *    suffixed keys are required in BOTH locales; Chinese has no plural
 *    inflection, so give `_one` and `_other` the same Chinese string. Do not
 *    invent `_zero` — zero is not a special grammatical form in either
 *    language, and "0 requests" must stay sayable (see below).
 *
 * 4. DOMAIN VOCABULARY — do not re-translate the spine of the product. If a
 *    string is one of the words under `domain.*`, reuse that key rather than
 *    writing 接口/系列/降级 again in your own namespace, so the same concept
 *    reads the same on every screen.
 *
 * 5. THE ONE DISTINCTION THAT MUST SURVIVE TRANSLATION — "no data" (无数据)
 *    and "0 requests" (0 次请求) are DIFFERENT statements and get different
 *    keys. This is a passive monitor: it reads Cloudflare's analytics and
 *    never sends a probe, so "we observed no traffic" is not "the endpoint
 *    answered nothing". Collapsing them in Chinese would turn every silent
 *    endpoint into a reported outage. `domain.noData` vs `common.requestCount`
 *    with `count: 0`.
 *
 * 6. ADDING KEYS — add to the section for your area in BOTH `en.ts` and
 *    `zh.ts`, keeping the two files in the same order so they diff side by
 *    side. Sections are fenced with banner comments; append inside yours, not
 *    at the end of the file, so twelve agents editing at once collide less.
 *
 * 7. KNOWN LIMIT: SERVER ERROR TEXT IS ENGLISH, AND THAT IS THE DECISION.
 *    Every error surface here (`dashboard.overview.error`,
 *    `dashboard.table.errorHttp`, `account.adminError.*`,
 *    `account.inviteAccept.error.invalid`) interpolates the backend's raw
 *    `detail` into a translated frame, so a Chinese user hitting a refusal
 *    reads a Chinese sentence wrapping an English one — "接口列表不可用 ——
 *    Snapshot unavailable". FastAPI is not translated and no frontend change
 *    can close that.
 *
 *    Keeping `detail` is deliberate and is the lesser evil: those sentences
 *    are specific and actionable ("cannot deactivate the last active
 *    superadmin", "database is locked"), and replacing them with a keyed
 *    euphemism would cost the admin the one thing that says what to do next.
 *    The keyed fallbacks beside them are for when the server sends no detail
 *    at all.
 *
 *    The real fix is backend-side and is FOLLOW-UP WORK, not an i18n bug to
 *    file again: have FastAPI return a stable machine `code` alongside
 *    `detail`, key the frontend off `code`, and fall back to `detail` for
 *    anything unmapped. Until that lands, mixed-language failure text on the
 *    error paths is expected behaviour.
 */
export const en = {
  // ── common ───────────────────────────────────────────────────────────────
  // UI furniture and verbs. Owned by nobody, reused everywhere.
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.confirm': 'Confirm',
  'common.continue': 'Continue',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.error': 'Something went wrong.',
  'common.loading': 'Loading…',
  'common.retry': 'Retry',
  'common.save': 'Save',
  'common.saving': 'Saving…',
  // The plural example. See rule 3.
  'common.requestCount_one': '{{count}} request',
  'common.requestCount_other': '{{count}} requests',
  'common.endpointCount_one': '{{count}} endpoint',
  'common.endpointCount_other': '{{count}} endpoints',

  // ── domain ───────────────────────────────────────────────────────────────
  // The product vocabulary. Reuse these keys; never re-translate the words.
  'domain.endpoint': 'Endpoint',
  'domain.family': 'Family',
  'domain.health': 'Health',
  'domain.health.healthy': 'Healthy',
  'domain.health.degraded': 'Degraded',
  'domain.health.down': 'Down',
  'domain.health.unknown': 'Unknown',
  'domain.requests': 'Requests',
  'domain.errorRate': 'Error rate',
  'domain.statusCode': 'Status code',
  'domain.window': 'Window',
  'domain.sampled': 'Sampled',
  'domain.estimated': 'Estimated',
  // Distinct from `common.requestCount` with count 0. See rule 5.
  'domain.noData': 'No data',
  'domain.trafficCliff': 'Traffic cliff',
  'domain.backupCodes': 'Backup codes',
  'domain.twoFactor': 'Two-factor authentication',
  'domain.twoFactorShort': '2FA',
  'domain.invite': 'Invite',
  'domain.role.superadmin': 'Superadmin',
  'domain.role.member': 'Member',

  // ── nav (src/nav) ────────────────────────────────────────────────────────
  'nav.header.title': 'API health',
  'nav.header.mainNav': 'Main',
  'nav.header.dashboard': 'Dashboard',
  'nav.header.profile': 'Profile',
  'nav.header.admin': 'Admin',
  'nav.header.signOut': 'Sign out',
  // The switcher's own label. The OPTIONS are never translated — a user who
  // cannot read the current language still has to be able to find their way
  // out, so "English" and "中文" are hardcoded in LanguageSwitcher.tsx.
  'nav.language.label': 'Language',
  // The browser tab, set from `runtime.tsx` on every language change. The
  // static `<title>` in index.html is what the tab says before React mounts;
  // this is what it says afterwards, in both languages. Longer than
  // `nav.header.title` on purpose: a tab among twenty tabs, or a bookmark read
  // months later, has no header beside it to say which product this is.
  'nav.document.title': 'API Health Monitor',

  // ── auth (src/auth) ──────────────────────────────────────────────────────
  // LoginPage, TwoFactorPage, RequireAuth, useAuth.

  // LoginPage — step 1, email + password.
  'auth.login.title': 'Sign in',
  'auth.login.subtitle': 'TikHub API health monitor — internal access only.',
  'auth.login.email': 'Email',
  'auth.login.password': 'Password',
  'auth.login.submit': 'Sign in',
  // The two "the server answered a gate but sent no token" refusals. Both are
  // dead ends by design: advancing would show a screen that can only 401.
  'auth.login.missingMfaToken': 'The server did not issue a two-factor token. Please try again.',
  'auth.login.missingEnrollmentToken':
    'This account must set up two-factor authentication, but the server did not issue an enrollment token. Please try again.',

  // TwoFactorPage — step 2, the TOTP code (or a backup code). The card title
  // is `domain.twoFactor`; do not duplicate the vocabulary here.
  'auth.twoFactor.subtitle': 'Enter the 6-digit code from your authenticator app.',
  'auth.twoFactor.codeLabel': 'Authentication code',
  'auth.twoFactor.codeHint':
    'Lost your phone? Use one of your 10-digit backup codes — each works once.',
  'auth.twoFactor.submit': 'Verify',
  'auth.twoFactor.useDifferentAccount': 'Use a different account',
  // `{{length}}` is MIN_CODE, so the sentence cannot drift from the check.
  'auth.twoFactor.codeTooShort': 'Enter the {{length}}-digit code from your authenticator app.',
  'auth.twoFactor.expired': 'Your sign-in attempt timed out. Please enter your password again.',

  // RequireAuth — the screen-reader-only label for the session check.
  'auth.requireAuth.checkingSession': 'Checking your session',

  // describeAuthError — shared by both screens. SECURITY COPY: one message
  // answers both a wrong address and a wrong password, because the backend
  // hashes a dummy password for unknown accounts so the timing matches. A
  // friendlier "no such account" in any language hands back the account
  // enumeration the server just spent effort preventing.
  'auth.error.badCredentials': 'Incorrect email or password.',
  'auth.error.badCode':
    'That code is not valid. Try the next one from your authenticator, or a backup code.',
  'auth.error.lockedOut': 'Too many failed attempts. This account is locked for 15 minutes.',
  'auth.error.invalidInput': 'Please check the details you entered.',
  'auth.error.failed': 'Sign-in failed ({{status}}). Please try again.',
  'auth.error.unreachable': 'Could not reach the server. Check your connection and try again.',

  // ── account (src/account) ────────────────────────────────────────────────
  // ProfilePage, AccountSummary, ChangePasswordForm, TwoFactorSetup,
  // BackupCodes, SecretReveal, InviteAcceptPage, EnrollmentStep, AdminPage,
  // TeamMemberRow, InviteMemberForm, ConfirmAction.

  // ConfirmAction. The phrase is rendered in mono inside the label, so the key
  // is split on `{{phrase}}` at render time rather than concatenated.
  'account.confirm.typePhrase': 'Type {{phrase}} to confirm',

  // AdminPage.
  'account.admin.denied':
    'Team management is superadmin only. Ask a superadmin if you need an account changed.',
  'account.admin.title': 'Team',
  'account.admin.lede':
    'Accounts, the three recovery actions, and the one that cannot be undone. Everything ' +
    'here is enforced again server-side.',
  'account.admin.membersTitle': 'Team members',
  'account.admin.accountCount_one': '{{count}} account',
  'account.admin.accountCount_other': '{{count}} accounts',
  'account.admin.dismiss': 'Dismiss',
  'account.admin.loading': 'Loading the team…',

  // The team table's columns. Shared with TeamMemberRow, whose flags restate
  // the column name in their accessible label — one string, one translation.
  'account.team.col.email': 'Email',
  'account.team.col.role': 'Role',
  'account.team.col.active': 'Active',
  'account.team.col.mustChangePassword': 'Must change password',
  'account.team.col.actions': 'Actions',

  // TeamMemberRow flags. "Off (required)" is a different account state from
  // "Off": locked out until it enrols, versus allowed to stay as it is.
  'account.team.flag.yes': 'Yes',
  'account.team.flag.no': 'No',
  'account.team.flag.on': 'On',
  'account.team.flag.off': 'Off',
  'account.team.flag.offRequired': 'Off (required)',
  'account.team.flag.aria': '{{label}}: {{state}}',

  'account.team.you': '(you)',
  'account.team.selfTitle': 'You cannot do this to your own account.',
  'account.team.selfNote':
    'Your own account: change your password or 2FA from your profile. The server refuses ' +
    'these three on the account making the request.',

  'account.team.resetPassword.label': 'Reset password',
  'account.team.resetPassword.question': 'Reset the password for {{email}}?',
  'account.team.resetPassword.detail':
    'A random temporary password is issued and shown once. Their current password ' +
    'stops working immediately, every signed-in session ends, and they must choose a ' +
    'new password on their next sign-in.',
  'account.team.resetPassword.confirm': 'Yes, reset password',

  'account.team.disable2fa.label': 'Disable 2FA',
  'account.team.disable2fa.question': 'Disable two-factor authentication for {{email}}?',
  'account.team.disable2fa.detail':
    'For a lost authenticator. The TOTP secret and any unused backup codes are ' +
    'destroyed and every session of theirs ends. If two-factor is required on ' +
    'their account they must set it up again at their next sign-in; otherwise ' +
    'the account is password-only until they enrol again.',
  'account.team.disable2fa.confirm': 'Yes, disable 2FA',
  'account.team.disable2fa.inapplicable': 'This account has no 2FA enabled.',
  'account.team.disable2fa.notice':
    'Two-factor authentication is off for {{email}}. They are signed out, and will be ' +
    'asked to set up two-factor authentication again the next time they sign in.',

  'account.team.deactivate.label': 'Deactivate',
  'account.team.deactivate.question': 'Deactivate {{email}}?',
  'account.team.deactivate.detail':
    'They are signed out everywhere immediately and cannot sign in again. ' +
    'Reactivating requires a shell on the server.',
  'account.team.deactivate.confirm': 'Yes, deactivate',
  'account.team.deactivate.inapplicable': 'This account is already deactivated.',
  'account.team.deactivate.notice': '{{email}} is deactivated and signed out (active: {{active}}).',

  'account.team.delete.label': 'Delete account',
  'account.team.delete.question': 'Permanently delete {{email}}?',
  'account.team.delete.detail':
    'This cannot be undone. The account, its sessions and its unused backup codes are ' +
    'destroyed, and any invite it sent that nobody has redeemed yet stops working. ' +
    'Its entries in the audit log are kept — detached from the account and labelled ' +
    'with this address — so the record of what it did survives the account. Deactivate ' +
    'instead if you only need them signed out.',
  'account.team.delete.confirm': 'Delete this account',

  // What the delete actually did. Two clauses because English inflects each of
  // them on its own count; the summary is one sentence per language, never a
  // pile of concatenated fragments.
  'account.team.deleted.invites_one': '{{count}} unused invite was revoked',
  'account.team.deleted.invites_other': '{{count}} unused invites were revoked',
  'account.team.deleted.audit_one':
    '{{count}} audit log entry was kept, labelled with this address and no longer linked ' +
    'to an account',
  'account.team.deleted.audit_other':
    '{{count}} audit log entries were kept, labelled with this address and no longer linked ' +
    'to an account',
  'account.team.deleted.summary': '{{email}} is permanently deleted. {{invites}}, and {{audit}}.',

  'account.team.tempPassword.title': 'Temporary password for {{email}}',
  'account.team.tempPassword.warning':
    'Shown once. Nothing stores this in readable form, so it cannot be looked ' +
    'up again — copy it now and hand it over in person or over a channel you ' +
    'trust. They must change it at their next sign-in.',
  'account.team.tempPassword.label': 'Temporary password',

  // InviteMemberForm.
  'account.invite.title': 'Add team member',
  'account.invite.lede':
    'There is no self-signup. An invite is the only way an account comes into existence.',
  'account.invite.email': 'Email',
  'account.invite.role': 'Role',
  'account.invite.submit': 'Send invite',
  'account.invite.reveal.title': 'Invite for {{email}}',
  'account.invite.reveal.warning':
    'Shown once. This token is the only way this person can ever register — ' +
    'copy it now and send it over a channel you trust. Closing this panel loses it, ' +
    'and the only fix is to issue a new invite.',
  'account.invite.linkLabel': 'Invite link',
  // `role` is the product's word for it (`domain.role.*`), the same word the
  // picker above and the team table show; a role this build does not know
  // falls back to the raw id. `expires` is Pacific, like every other timestamp.
  'account.invite.linkHint': 'Expires {{expires}} · role: {{role}}',
  'account.invite.tokenLabel': 'Invite token',
  'account.invite.tokenHint': 'For anyone who would rather paste the token than follow a link.',

  // Admin-surface request failures. The backend's own sentence is preferred
  // over all of these; they are only what is said when there is not one.
  'account.adminError.forbidden': 'Only a superadmin can do this.',
  'account.adminError.invalid': 'Please check the details you entered.',
  'account.adminError.server': 'The server could not complete that ({{status}}). Please try again.',
  'account.adminError.failed': 'Request failed ({{status}}).',
  'account.adminError.network': 'Could not reach the server. Check your connection and try again.',

  // InviteAcceptPage.
  'account.inviteAccept.title': 'Accept your invite',
  'account.inviteAccept.subtitle':
    'TikHub API health monitor — this link is the only way to create your account, and ' +
    'two-factor authentication is set up as part of creating it.',
  'account.inviteAccept.tokenLabel': 'Invite token',
  'account.inviteAccept.tokenHint':
    'Filled in from your invite link. Paste it by hand if you only have the token.',
  'account.inviteAccept.passwordLabel': 'Choose a password',
  'account.inviteAccept.passwordHint': 'At least {{min}} characters.',
  'account.inviteAccept.confirmLabel': 'Confirm password',
  'account.inviteAccept.submit': 'Create account',
  'account.inviteAccept.already': 'Already created your account?',
  'account.inviteAccept.signIn': 'Sign in',
  'account.inviteAccept.created.title': 'Account created',
  'account.inviteAccept.created.fallbackSubject': 'Your account',
  'account.inviteAccept.created.body':
    '{{subject}} is ready, but two-factor authentication is still required and this page ' +
    'did not receive an enrollment link.',
  'account.inviteAccept.created.next':
    'Sign in with the password you just chose to finish setting up two-factor authentication.',
  'account.inviteAccept.error.noToken':
    'Paste the invite token you were sent — the link should have filled it in.',
  'account.inviteAccept.error.short': 'Your password must be at least {{min}} characters.',
  'account.inviteAccept.error.mismatch': 'The password and its confirmation do not match.',
  'account.inviteAccept.error.invalidFallback': 'This invite cannot be used.',
  'account.inviteAccept.error.invalid':
    '{{detail}} If you have already created your account with this link, sign in instead; ' +
    'otherwise ask whoever invited you for a new one.',
  'account.inviteAccept.error.exists':
    'An account with that email already exists. Sign in instead, or ask for a reset.',
  'account.inviteAccept.error.server':
    'Could not create your account ({{status}}). Please try again.',
  'account.inviteAccept.error.network':
    'Could not reach the server. Check your connection and try again.',

  // TwoFactorSetup. The h2 is `domain.twoFactor`, not a key of its own.
  'account.twoFactorSetup.requirementRequired':
    'Required on this account: a code from your authenticator app, on top of your ' +
    'password, every time you sign in. It cannot be turned off or set up later.',
  'account.twoFactorSetup.requirement':
    'A code from your authenticator app, on top of your password, every time you sign in.',
  'account.twoFactorSetup.signInAgain': 'Sign in again',
  'account.twoFactorSetup.enabledNow': 'Two-factor authentication is now enabled on your account.',
  'account.twoFactorSetup.acknowledge': 'I have saved my backup codes — continue',
  'account.twoFactorSetup.start': 'Set up two-factor authentication',
  'account.twoFactorSetup.step1': '1. Scan this QR code with your authenticator app.',
  'account.twoFactorSetup.qrAlt': 'QR code for two-factor enrollment',
  'account.twoFactorSetup.step2': '2. Or enter this key by hand.',
  // Passed to CopyButton, which renders it inside "Copy {{label}}".
  'account.twoFactorSetup.setupKey': 'setup key',
  'account.twoFactorSetup.step3': '3. Enter the code your app shows.',
  'account.twoFactorSetup.codeLabel': 'Authentication code',
  'account.twoFactorSetup.submit': 'Confirm and enable',
  'account.twoFactorSetup.enabledTitle': 'Enabled',
  'account.twoFactorSetup.enabledHelp':
    'Your account asks for an authenticator code at every sign-in. Lost your phone? Use a ' +
    'backup code to sign in, or ask a superadmin to reset two-factor on your account.',
  // `{{length}}` is CODE_LENGTH, so the sentence cannot drift from the input.
  'account.twoFactorSetup.error.codeLength':
    'Enter the {{length}}-digit code from your authenticator app.',
  'account.twoFactorSetup.error.expiredLink':
    'This enrollment link has expired. Sign in again to finish setting up ' +
    'two-factor authentication.',
  'account.twoFactorSetup.error.alreadyEnabledReload':
    'Two-factor authentication is already enabled on this account. Reload the page.',
  'account.twoFactorSetup.error.mustChangePassword':
    'Change your password first — the rest of your account is locked until you do.',
  'account.twoFactorSetup.error.sessionExpired':
    'Your session has expired. Please sign in again.',
  'account.twoFactorSetup.error.enrollFailed':
    'Could not start enrollment ({{status}}). Please try again.',
  'account.twoFactorSetup.error.network':
    'Could not reach the server. Check your connection and try again.',
  'account.twoFactorSetup.error.codeRejectedToken':
    'That code was not accepted. Make sure you are reading the code for the QR code on ' +
    'this page — an entry added from an earlier attempt will no longer work — then ' +
    'check your phone’s clock and try the next code.',
  'account.twoFactorSetup.error.codeRejected':
    'That code was not accepted. Check your phone’s clock and try the next code.',
  'account.twoFactorSetup.error.alreadyEnabled':
    'Two-factor authentication is already enabled on this account.',
  'account.twoFactorSetup.error.confirmFailed':
    'Could not enable two-factor authentication ({{status}}). Please try again.',

  // BackupCodes. The warning is the most consequential sentence in the app:
  // there is no re-issue endpoint, so a user who misreads it is locked out.
  'account.backupCodes.warning':
    'Save these backup codes now. This is the only time they are shown — they are stored ' +
    'hashed and cannot be shown again. Each code works once, in place of a code from your ' +
    'authenticator app.',
  'account.backupCodes.copy': 'Copy codes',
  'account.backupCodes.download': 'Download codes',
  'account.backupCodes.copied': 'Copied to your clipboard.',
  'account.backupCodes.copyFailed':
    'Could not reach the clipboard — select the codes above and copy them by hand.',

  // SecretReveal. `title` and `warning` are props, translated by the callers.
  'account.secretReveal.dismiss': 'Dismiss',
  'account.secretReveal.copyValue': 'Copy {{label}}',
  'account.secretReveal.copyFailed': 'Copy failed — select it and copy by hand.',

  // EnrollmentStep. Whole sentences, not fragments joined around the address.
  'account.enrollment.title': 'Finish setting up your account',
  'account.enrollment.intro':
    'Two-factor authentication is required before you can use the monitor — there is no ' +
    'way to skip this step or come back to it later.',
  'account.enrollment.introWithEmail':
    '{{email}} is ready. Two-factor authentication is required before you can use the ' +
    'monitor — there is no way to skip this step or come back to it later.',

  // ProfilePage — the forced-rotation banner.
  'account.profile.rotationTitle': 'Change your password now',
  'account.profile.rotationBody':
    'This account is still using a password someone else issued — an invite or an admin reset. ' +
    'The rest of the app is unavailable until you replace it: the server refuses every other ' +
    'request while this flag is set, and the nav links will bring you straight back here. ' +
    'Choose your own password below.',

  // AccountSummary. The 2FA row's own label is `domain.twoFactor`, and the
  // role values are `domain.role.*` — do not re-translate either here.
  'account.summary.title': 'Account',
  'account.summary.note':
    'Accounts are created by invitation only; ask a superadmin to change your email or role.',
  'account.summary.email': 'Email',
  'account.summary.role': 'Role',
  'account.summary.twoFactorEnabled': 'Enabled',
  'account.summary.twoFactorDisabled': 'Not enabled',

  // ChangePasswordForm. `{{min}}` is MIN_PASSWORD_LENGTH — the one floor every
  // password path enforces, so it is interpolated and never written out.
  'account.changePassword.title': 'Change password',
  'account.changePassword.note':
    'Changing your password signs you out everywhere else. This browser stays signed in.',
  'account.changePassword.currentLabel': 'Current password',
  'account.changePassword.newLabel': 'New password',
  'account.changePassword.newHint': 'At least {{min}} characters.',
  'account.changePassword.confirmLabel': 'Confirm new password',
  'account.changePassword.submit': 'Change password',
  'account.changePassword.tooShort': 'Your new password must be at least {{min}} characters.',
  'account.changePassword.mismatch': 'The new password and its confirmation do not match.',
  'account.changePassword.success':
    'Your password has been changed. Every other session has been signed out.',
  'account.changePassword.errorWrongCurrent': 'That is not your current password.',
  'account.changePassword.errorSessionExpired': 'Your session has expired. Please sign in again.',
  'account.changePassword.errorStatus':
    'Could not change your password ({{status}}). Please try again.',
  'account.changePassword.errorNetwork':
    'Could not reach the server. Check your connection and try again.',

  // ── dashboard (src/dashboard) ────────────────────────────────────────────
  // DashboardPage, Overview, EndpointTable, EndpointRow, FamilyGroup,
  // EndpointDetail, StatusPie, StatusTimeSeries, StatusRatioBar, HealthBadge,
  // FreshnessBadge, WindowPicker, StatusFilter.


  // EndpointTable. Column headers reuse `domain.*` where the word is the
  // vocabulary itself (Health, Endpoint, Requests); the three that are not
  // domain words are keyed here.
  'dashboard.table.aria': 'Endpoints',
  'dashboard.table.colMix': 'Mix',
  'dashboard.table.colErrors': 'Errors',
  'dashboard.table.colTrend': 'Trend',
  'dashboard.table.sortHint': 'Sort by this column. Hold Shift to add a second key.',
  // One key, three placeholders: Chinese puts the counts in a different order,
  // which a sentence glued together from fragments could not express.
  'dashboard.table.summary': '{{visible}} of {{total}} endpoints in {{families}} families',
  'dashboard.table.searchLabel': 'Filter endpoints by path or family',
  'dashboard.table.searchPlaceholder': 'Filter by path or family…',
  'dashboard.table.expandAll': 'Expand all',
  'dashboard.table.collapseAll': 'Collapse all',
  'dashboard.table.emptyFiltered': 'No endpoints match the current filters.',
  'dashboard.table.emptySearch': 'No endpoint path or family matches “{{query}}”.',
  'dashboard.table.errorTitle': 'Endpoint list unavailable — {{detail}}',
  'dashboard.table.errorHttp': 'HTTP {{status}}',
  'dashboard.table.errorUnreachable': 'Could not reach the server.',

  // EndpointRow. `noData` is the em-dash tooltip: an absence of measurement,
  // never a measurement of zero — see rule 5.
  'dashboard.row.noData': 'No data for this endpoint in this window.',
  'dashboard.row.noRequestsInBucket': 'No requests in this bucket.',
  'dashboard.row.cliffNotApplicable':
    'Traffic-cliff detection compares this window against the 24h baseline, so it only applies to windows shorter than 24h.',
  'dashboard.row.cliffNoBaseline':
    'No traffic baseline for this endpoint, so there is nothing to compare against.',
  'dashboard.row.cliffTitle':
    "Traffic in this window is far below the 24h baseline rate — the endpoint may be failing before it reaches Cloudflare's logs.",
  // The lower-case badge form of `domain.trafficCliff`. A separate key only
  // because English casing differs; the Chinese string is the domain word.
  'dashboard.row.cliffBadge': 'traffic cliff',
  'dashboard.row.steady': 'steady',

  // FamilyGroup.
  'dashboard.family.statusNone': 'No {{bucket}} data measured for this family in this window.',
  'dashboard.family.statusTotal': '{{count}} {{bucket}} responses across this family.',
  'dashboard.family.count': '{{visible}} of {{total}}',
  'dashboard.family.chipTitle': '{{count}} {{state}} — {{hint}}',
  'dashboard.family.totalNone':
    'No endpoint in this family returned data for this window, so there is no total to report. That is not the same as zero requests.',
  'dashboard.family.totalPartial':
    'Requests across the {{measured}} of {{total}} endpoints in this family that returned data for this window. The rest were not measured.',
  'dashboard.family.totalAll': 'Requests across every endpoint in this family in this window.',

  // StatusRatioBar. `noData` (we looked at nothing) and `common.requestCount`
  // at zero (we looked, and it served nothing) are the two halves of rule 5 and
  // must never be merged.
  'dashboard.ratioBar.noData': 'no data',
  'dashboard.ratioBar.noDataTitle':
    'Cloudflare reported no requests for this endpoint in this window. That is an absence of evidence, not evidence of zero traffic.',
  'dashboard.ratioBar.zeroTitle': 'This endpoint served no requests in this window.',
  'dashboard.ratioBar.noStatusDetail': 'no status detail',
  'dashboard.ratioBar.segment': '{{bucket}} {{share}}',
  // A list separator, not a sentence: Chinese enumerates with a dun comma.
  'dashboard.ratioBar.separator': ', ',
  'dashboard.ratioBar.title': '{{total}} requests — {{breakdown}}',

  // FreshnessBadge. The age fragments are self-contained quantities, so
  // `behind` can wrap one without becoming a glued-together sentence.
  'dashboard.freshness.noTimestamp': 'timestamp unavailable',
  'dashboard.freshness.asOf': 'as of {{time}} PT',
  'dashboard.freshness.behind': '{{age}} behind',
  'dashboard.freshness.lagTitle':
    'Cloudflare analytics finalize on a delay, so the window always ends in the past.',
  'dashboard.freshness.ageSeconds': '{{n}} sec',
  'dashboard.freshness.ageMinutes': '{{n}} min',
  'dashboard.freshness.ageHours': '{{n}} h',
  'dashboard.freshness.ageUnknown': 'unknown time',
  'dashboard.freshness.estimated': '~estimated',
  'dashboard.freshness.sampleRate': 'roughly 1 in {{n}} requests',
  'dashboard.freshness.sampleRateUnknown': 'a subset of requests',
  'dashboard.freshness.sampleTitle':
    'Cloudflare sampled this window ({{rate}}). Every count here is scaled up from that sample and is a statistical estimate, not an exact tally.',
  'dashboard.freshness.stale': 'stale — Cloudflare unreachable',

  // EndpointDetail. `requestsLabel`, `errorsLabel` and `trafficCliff` are the
  // lower-case renderings of `domain.requests` / `domain.errorRate` /
  // `domain.trafficCliff` — the tiles set them in running text, not as column
  // headings, and the English copy is preserved verbatim. The Chinese side
  // reuses the domain vocabulary word for word.
  'dashboard.detail.regionLabel': 'Endpoint detail',
  'dashboard.detail.healthWindowHint': 'Health above is for the window this page is pinned to.',
  'dashboard.detail.inWindow': 'in {{window}}',
  'dashboard.detail.windowCardTitle': '{{window}} — show this window in the pie below',
  'dashboard.detail.notCached': 'not cached yet',
  'dashboard.detail.notCachedTitle':
    'This window is not in the backend cache yet, so it was not returned. It will appear once the scheduler warms it.',
  'dashboard.detail.noDataTitle': 'No data for this endpoint in this window.',
  'dashboard.detail.requestsLabel': 'requests',
  'dashboard.detail.errorsLabel': 'errors',
  'dashboard.detail.trafficCliff': 'traffic cliff',
  'dashboard.detail.trafficCliffTitle':
    "Traffic in this window is far below the 24h baseline rate — the endpoint may be failing before it reaches Cloudflare's logs.",
  'dashboard.detail.unreachable': 'Could not reach the server.',
  'dashboard.detail.gone': 'This endpoint is no longer in the registry — {{detail}}.',
  'dashboard.detail.unavailable': 'Endpoint detail unavailable — {{detail}}',

  // The shares/counts toggle, worded identically on the pie and on the bars.
  'dashboard.scale.shares': 'Shares',
  'dashboard.scale.counts': 'Counts',

  // StatusPie. `footer` is one sentence with two placeholders rather than three
  // concatenated fragments: the numbers keep their own styling, and Chinese
  // gets to put them where Chinese puts them.
  'dashboard.pie.regionLabel': 'Status mix for the selected window',
  'dashboard.pie.heading': 'Status mix',
  'dashboard.pie.headingWindow': '({{window}})',
  'dashboard.pie.scaleGroup': 'Pie scale',
  'dashboard.pie.emptyNotCached':
    'This window is not in the backend cache yet, so there is nothing to draw.',
  'dashboard.pie.emptyNoRequests':
    'No requests recorded in this window, so there is no composition to draw.',
  'dashboard.pie.footer_one': '{{total}} requests across {{codes}} status code',
  'dashboard.pie.footer_other': '{{total}} requests across {{codes}} status codes',

  // StatusTimeSeries. `noRequests` is the measured-and-silent case and must stay
  // distinct from `notMeasured` / `domain.noData` in both languages — see rule 5.
  'dashboard.timeSeries.heading': 'Status mix by window',
  'dashboard.timeSeries.headingHint': '(recent on the left, baseline on the right)',
  'dashboard.timeSeries.headingTitle':
    'The six windows are nested — 7d contains 24h contains 30m — so the bars are six views of the same endpoint, not six slices of time.',
  'dashboard.timeSeries.scaleGroup': 'Bar scale',
  'dashboard.timeSeries.empty':
    'No request data in any cached window, so there is no composition to draw.',
  'dashboard.timeSeries.notMeasured': 'not measured',
  'dashboard.timeSeries.noRequests': 'no requests',

  // DashboardPage / Overview / StatusFilter / WindowPicker.
  'dashboard.page.detailPanel': 'Endpoint detail',

  'dashboard.windowPicker.label': 'Time window',

  'dashboard.statusFilter.noneSelected': 'No health states selected — the table is empty.',
  'dashboard.statusFilter.showAll': 'Show all',

  'dashboard.overview.label': 'Overview',
  // The unit beside the big number, lower-case and on its own line in the
  // markup — not `domain.requests`, which is a column heading.
  'dashboard.overview.requests': 'requests',
  // "We saw the window and it was silent", NOT `domain.noData`. See rule 5.
  'dashboard.overview.noRequests': 'No requests in this window.',
  'dashboard.overview.composition': 'Status composition: {{parts}}',
  'dashboard.overview.compositionPart': '{{label}} {{share}}',
  // Locale punctuation, not a sentence fragment: Chinese lists take a 、
  'dashboard.overview.listSeparator': ', ',
  'dashboard.overview.bucketTooltip': '{{label}}: {{count}} ({{share}})',
  'dashboard.overview.tileShare': '{{share}} of {{total}} endpoints',
  'dashboard.overview.tileNoEndpoints': 'no endpoints',
  'dashboard.overview.unreachable': 'Could not reach the server.',
  'dashboard.overview.error': 'Overview unavailable — {{detail}}',

  // The `hint` strings of HEALTH_STYLES, and the `description` strings of
  // BUCKET_STYLES, in `statusColors.ts`. Keyed here rather than in that module
  // so the palette stays a palette; every dashboard component that shows a
  // health tooltip or a bucket tooltip should reuse these two families. The
  // bucket `label`s ("2xx", "429") are technical tokens and stay untranslated.
  'dashboard.healthHint.down': 'Server errors dominate, or every request in the window failed.',
  'dashboard.healthHint.degraded': 'Error rate above the healthy threshold but still serving.',
  'dashboard.healthHint.healthy': 'Errors within normal bounds.',
  'dashboard.healthHint.unknown':
    'Too little traffic in this window to judge. Not the same as healthy.',
  'dashboard.statusBucket.2xx': 'Success',
  'dashboard.statusBucket.3xx': 'Redirect',
  'dashboard.statusBucket.4xx': 'Client error',
  'dashboard.statusBucket.429': 'Rate limited or quota exhausted (429, 402)',
  'dashboard.statusBucket.402': 'Payment required — the caller is out of quota or credit.',
  'dashboard.statusColumn.200': 'Responses that returned exactly 200.',
  'dashboard.statusColumn.400': 'Responses that returned exactly 400 — a malformed request.',
  'dashboard.statusColumn.401': 'Responses that returned 401 — the caller was not authenticated.',
  'dashboard.statusColumn.403': 'Responses that returned 403 — the caller was authenticated but not permitted.',
  'dashboard.statusColumn.404': 'Responses that returned 404 — no such resource.',
  'dashboard.statusColumn.405': 'Responses that returned 405 — wrong HTTP method for this endpoint.',
  'dashboard.statusColumn.402': 'Responses that returned 402 — the caller is out of quota or credit.',
  'dashboard.statusColumn.429': 'Responses that returned 429 — the caller was rate limited.',
  'dashboard.statusBucket.5xx': 'Server error',

  // ── api (src/api) ────────────────────────────────────────────────────────
  // Window labels and client error text. Note that these live in modules, not
  // components: keep the KEY in the data (`Record<WindowId, TranslationKey>`)
  // and call `t()` at render time, so a language switch re-renders them.

  // `WINDOW_LABEL_KEYS` in `api/types.ts` maps each WindowId to one of these.
  // The ids themselves ('30m', '7d') are API values and are never translated.
  'api.window.30m': 'Last 30 minutes',
  'api.window.6h': 'Last 6 hours',
  'api.window.12h': 'Last 12 hours',
  'api.window.24h': 'Last 24 hours',
  'api.window.72h': 'Last 72 hours',
  'api.window.7d': 'Last 7 days',
}

/**
 * Every key a locale file must define.
 *
 * `zh.ts` is typed `Record<LocaleKey, string>`, so this union is what makes an
 * untranslated key a compile error rather than a silent English leak.
 */
export type LocaleKey = keyof typeof en
